import { useEffect, useRef } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

type CurationDraftType = 'goods' | 'snap';

const SHOP_EDIT_FORM_FLOW_PATH_PATTERNS = [
  /^\/shop-edit\/?$/,
  /^\/shop-edit\/new\/(?:goods|snap)\/?$/,
  /^\/shop-edit\/(?!new\/?$)[^/]+\/?$/,
  /^\/curator\/upload\/good\/?$/,
  /^\/curator\/upload\/(?:goods|snap)\/?$/,
  /^\/curator\/shop-edit\/curation\/[^/]+\/?$/,
];

export const getCurationFormDraftKey = (type: CurationDraftType, curationId?: string): string =>
  curationId
    ? `curator:shopEdit:${type}:edit:${curationId}:v1`
    : `curator:shopEdit:${type}:create:v1`;

const isShopEditFormFlowPath = (pathname: string): boolean =>
  SHOP_EDIT_FORM_FLOW_PATH_PATTERNS.some((pattern) => pattern.test(pathname));

const readDraft = <T extends FieldValues>(key: string, fallback: T): T => {
  if (typeof window === 'undefined') {
    return fallback;
  }

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) {
      return fallback;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return fallback;
    }

    return { ...fallback, ...parsed };
  } catch {
    return fallback;
  }
};

const writeDraft = (key: string, value: unknown): void => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage quota/security failures should not block the in-memory form.
  }
};

export const useCurationFormDraft = <T extends FieldValues>(
  key: string | null,
  form: UseFormReturn<T>,
  initialValues: T,
) => {
  const hydratedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!key || hydratedKeyRef.current === key) {
      return;
    }

    form.reset(readDraft(key, initialValues));
    hydratedKeyRef.current = key;
  }, [form, initialValues, key]);

  useEffect(() => {
    if (!key) {
      return;
    }

    const subscription = form.watch((value) => {
      writeDraft(key, value);
    });

    return () => subscription.unsubscribe();
  }, [form, key]);

  useEffect(() => {
    if (!key || typeof window === 'undefined') {
      return;
    }

    return () => {
      window.setTimeout(() => {
        if (!isShopEditFormFlowPath(window.location.pathname)) {
          try {
            window.sessionStorage.removeItem(key);
          } catch {
            // Storage access can fail in restricted browser contexts.
          }
        }
      }, 0);
    };
  }, [key]);
};
